/**
 * @module builder
 */
import type { TieredPackageIcon, TieredPackagePriceDisplay, TieredPackageTierId } from 'paintscout'

/**
 * Presentation tokens for package cards: the accent-color ramp, the font table, and the
 * icon set. Kept in the builder so the settings preview and the customer-facing card derive
 * identical colors from the same stored value.
 */

export interface TieredPackageColor {
  key: string
  /** The stored swatch color. */
  base: string
  /** Darkened for text and the recommended button. */
  dark: string
  /** Gradient stops for the recommended header. */
  vivid1: string
  vivid2: string
}

export const TIER_COLOR_SWATCHES: Array<{ key: string; base: string }> = [
  { key: 'blue', base: '#2377fd' },
  { key: 'indigo', base: '#4f46e5' },
  { key: 'teal', base: '#0f9d8f' },
  { key: 'green', base: '#16a34a' },
  { key: 'amber', base: '#d97706' },
  { key: 'rose', base: '#e11d48' },
  { key: 'purple', base: '#7c3aed' },
  { key: 'slate', base: '#475569' }
]

const HEX = /^#[0-9a-f]{6}$/i

function hexToRgb(hex: string) {
  const value = parseInt(hex.replace('#', ''), 16)
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 }
}

function rgbToHex(r: number, g: number, b: number) {
  const part = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0')
  return `#${part(r)}${part(g)}${part(b)}`
}

function rgbToHsl(r: number, g: number, b: number) {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  const delta = max - min
  let h = 0
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    h = max === rn ? ((gn - bn) / delta) % 6 : max === gn ? (bn - rn) / delta + 2 : (rn - gn) / delta + 4
    h *= 60
    if (h < 0) {
      h += 360
    }
  }
  return { h, s, l }
}

function hslToRgb(h: number, s: number, l: number) {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let rgb: [number, number, number]
  if (h < 60) {
    rgb = [c, x, 0]
  } else if (h < 120) {
    rgb = [x, c, 0]
  } else if (h < 180) {
    rgb = [0, c, x]
  } else if (h < 240) {
    rgb = [0, x, c]
  } else if (h < 300) {
    rgb = [x, 0, c]
  } else {
    rgb = [c, 0, x]
  }
  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 }
}

/** Same color at a given alpha, for the tinted header/button fills. */
export function withAlpha(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Resolve a stored color (swatch key or #rrggbb) into the ramp the cards render with. */
export function getTieredPackageColor(color?: string): TieredPackageColor {
  const swatch = color && HEX.test(color) ? { key: color, base: color } : null
  const resolved = swatch ?? TIER_COLOR_SWATCHES.find((s) => s.key === color) ?? TIER_COLOR_SWATCHES[0]
  const { r, g, b } = hexToRgb(resolved.base)
  const { h, s, l } = rgbToHsl(r, g, b)
  const darkRgb = hslToRgb(h, Math.max(0, s - 0.18), Math.max(0, l - 0.16))
  const vivid1Rgb = hslToRgb(h, 0.85, 0.7)
  const vivid2Rgb = hslToRgb(h, 1, 0.4)
  return {
    key: resolved.key,
    base: resolved.base,
    dark: rgbToHex(darkRgb.r, darkRgb.g, darkRgb.b),
    vivid1: rgbToHex(vivid1Rgb.r, vivid1Rgb.g, vivid1Rgb.b),
    vivid2: rgbToHex(vivid2Rgb.r, vivid2Rgb.g, vivid2Rgb.b)
  }
}

export interface TieredPackageFont {
  key: string
  label: string
  family: string
  /**
   * Webfontloader family spec (`Family:weights`) for FontLoad, or undefined for fonts that
   * need no loading (the theme default and system serifs).
   */
  webfont?: string
  /** Size used for the package name on the card. */
  cardSize: number
}

export const TIER_FONTS: TieredPackageFont[] = [
  { key: 'default', label: 'Default', family: 'inherit', cardSize: 18 },
  { key: 'roboto', label: 'Roboto', family: "'Roboto', sans-serif", webfont: 'Roboto:400,500', cardSize: 18 },
  {
    key: 'robotoMono',
    label: 'Roboto Mono',
    family: "'Roboto Mono', monospace",
    webfont: 'Roboto Mono:400,500',
    cardSize: 16
  },
  {
    key: 'workSans',
    label: 'Work Sans',
    family: "'Work Sans', sans-serif",
    webfont: 'Work Sans:400,500',
    cardSize: 18
  },
  { key: 'spectral', label: 'Spectral', family: "'Spectral', serif", webfont: 'Spectral:400,500', cardSize: 18 },
  { key: 'karla', label: 'Karla', family: "'Karla', sans-serif", webfont: 'Karla:400,500', cardSize: 18 },
  { key: 'georgia', label: 'Georgia', family: 'Georgia, serif', cardSize: 18 }
]

export const getTieredPackageFont = (key?: string): TieredPackageFont =>
  TIER_FONTS.find((f) => f.key === key) ?? TIER_FONTS[0]

/** Family specs to hand to `<FontLoad fonts={...}>` so the picker and cards render for real. */
export const TIER_FONT_WEBFONTS = TIER_FONTS.map((font) => font.webfont).filter(Boolean) as string[]

export const TIER_ICONS: Array<{ key: TieredPackageIcon; label: string }> = [
  { key: 'badge', label: 'Badge' },
  { key: 'badgeCheck', label: 'Badge check' },
  { key: 'star', label: 'Star' },
  { key: 'moonNew', label: 'Circle' },
  { key: 'sparkle', label: 'Sparkle' },
  { key: 'starOfLife', label: 'Star of life' },
  { key: 'certificate', label: 'Certificate' },
  { key: 'shield', label: 'Shield' }
]

export const TIER_BADGE_LABELS = ['Recommended', 'Most popular', 'Best Value']

/**
 * Stand-in prices for the settings preview. A template has no estimate behind it, so the
 * cards show a worked example instead — round numbers that make each price display legible.
 */
export const TIER_PREVIEW_PRICES: Record<TieredPackageTierId, number> = {
  low: 1000,
  mid: 1500,
  high: 2000
}

/**
 * How one tier's price reads under a given display mode, relative to the cheapest tier, split
 * into the parts a card may wrap between: `['$1,500']`, `['+$500']`, `['+50%']`, or for 'both'
 * `['$1,500', '(+$500)']`. Callers that just want a string can join it — see
 * formatTierPriceDisplay.
 */
export function formatTierPriceParts(args: {
  price: number
  basePrice: number
  priceDisplay: TieredPackagePriceDisplay
  format: (value: number) => string
}): string[] {
  const { price, basePrice, priceDisplay, format } = args
  const delta = price - basePrice

  if (!delta || priceDisplay === 'total') {
    return [format(price)]
  }

  const sign = delta > 0 ? '+' : '-'
  const difference = `${sign}${format(Math.abs(delta))}`

  if (priceDisplay === 'difference') {
    return [difference]
  }
  if (priceDisplay === 'percentage') {
    const percent = basePrice ? Math.round((delta / basePrice) * 100) : 0
    return [`${sign}${Math.abs(percent)}%`]
  }
  return [format(price), `(${difference})`]
}

/** formatTierPriceParts as a single string, e.g. `$1,500 (+$500)`. */
export function formatTierPriceDisplay(args: Parameters<typeof formatTierPriceParts>[0]): string {
  return formatTierPriceParts(args).join(' ')
}
